import { ArrowDownTrayIcon, XCircleIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import Drawer from "../Drawer";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { setReportNotificationsEnabled } from "../../slices/reportEmailSettings";
import ReactTooltip from "react-tooltip";
import { validateEmail } from "../../helpers/management/vendors";
import { SelectDropdown } from "../SelectDropdown";
import { arrayHasItems } from "../../helpers/common";
import { OverlayGrayBackground } from "../OverlayGrayBackground";
import { LoaderSpinner } from "../LoaderSpinner";
import { SHIPPING_EACH_COST_CHANGE } from "../../consts/reports";
import { SCHEDULABLE_REPORTS } from "../../consts/reportScheduling";
import { ReportSheetSyncSettings } from "./ReportSheetSyncSettings";

const getTypeColor = (type) => {
    switch (type) {
        case "ORIGINAL":
            return "bg-primary text-white";
        case "PREVIEW":
            return "bg-blue-500 text-white";
        case "SLACK":
            return "bg-purple-600 text-white";
        case "SLACK_PREVIEW":
            return "bg-purple-400 text-white";
        default:
            return "";
    }
}

const getTypeLabel = (type) => {
    switch (type) {
        case "ORIGINAL":
            return "Original";
        case "PREVIEW":
            return "Preview";
        case "SLACK":
            return "Slack";
        case "SLACK_PREVIEW":
            return "Slack Preview";
        default:
            return "";
    }
}

const daysOfWeek = [
    { label: "Monday", value: 1 },
    { label: "Tuesday", value: 2 },
    { label: "Wednesday", value: 3 },
    { label: "Thursday", value: 4 },
    { label: "Friday", value: 5 },
    { label: "Saturday", value: 6 },
    { label: "Sunday", value: 7 },
];

const daysOfMonth = [
    { label: "1", value: 1 },
    { label: "2", value: 2 },
    { label: "3", value: 3 },
    { label: "4", value: 4 },
    { label: "5", value: 5 },
    { label: "6", value: 6 },
    { label: "7", value: 7 },
    { label: "8", value: 8 },
    { label: "9", value: 9 },
    { label: "10", value: 10 },
    { label: "11", value: 11 },
    { label: "12", value: 12 },
    { label: "13", value: 13 },
    { label: "14", value: 14 },
    { label: "15", value: 15 },
    { label: "16", value: 16 },
    { label: "17", value: 17 },
    { label: "18", value: 18 },
    { label: "19", value: 19 },
    { label: "20", value: 20 },
    { label: "21", value: 21 },
    { label: "22", value: 22 },
    { label: "23", value: 23 },
    { label: "24", value: 24 },
    { label: "25", value: 25 },
    { label: "26", value: 26 },
    { label: "27", value: 27 },
    { label: "28", value: 28 },
];

const SendHistoryStatus = {
    SENT: "Sent",
    ERROR: "Failed",
};

export const ReportEmailSettingsSidebar = ({
  reportType,
  onClose,
  marginFromSidebar,
  onSuccessSubmit,
  sendHistory,
  sendPreviewEmail,
  sendPreviewSlack,
  isLoading = false,
  isOpen,
  dailyOptionEnabled = false,
  defaultSettings = {},
  isLoadingSendHistory = false,
  downloadFilenamePrefix = 'report',
  sheetSyncHandlerParams = {},
}) => {

    const dispatch = useDispatch();
    const [emailRecipients, setEmailRecipients] = useState([]);
    const [notificationsEnabled, setNotificationsEnabled] = useState(true);
    const [isSavingNotifications, setIsSavingNotifications] = useState(false);

    const handleToggleNotifications = async () => {
        if (!reportType || isSavingNotifications) return;
        const next = !notificationsEnabled;
        setNotificationsEnabled(next);
        setIsSavingNotifications(true);
        try {
            await dispatch(setReportNotificationsEnabled({ reportType, enabled: next })).unwrap();
            toast.success(next ? 'Notifications turned on' : 'Notifications turned off', { position: 'bottom-right' });
        } catch (e) {
            setNotificationsEnabled(!next);
            toast.error(typeof e?.error === 'string' ? e.error : 'Failed to update notifications setting', { position: 'bottom-right' });
        } finally {
            setIsSavingNotifications(false);
        }
    };
    const [emailRecipientInput, setEmailRecipientInput] = useState('');
    const [errors, setErrors] = useState({
        email: null,
        previewEmail: null,
        previewSlack: null,
    });
    const [schedule, setSchedule] = useState("monthly");
    const [slackChannelId, setSlackChannelId] = useState('');
    const [emailSendPreviewTo, setEmailSendPreviewTo] = useState('');
    const [slackSendPreviewTo, setSlackSendPreviewTo] = useState('');
    const [dayOfMonth, setDayOfMonth] = useState(1);
    const [dayOfWeek, setDayOfWeek] = useState(1);
    const [timeOfDay, setTimeOfDay] = useState("13:00");
    const [emailMessage, setEmailMessage] = useState('');
    const [hidePreview, setHidePreview] = useState(false);
    const [thresholdValueDollar, setThresholdValueDollar] = useState(0);
    const [thresholdValuePercentage, setThresholdValuePercentage] = useState(0);

    const sendPreviewEmailAction = (email) => {
        sendPreviewEmail?.({
            emailMessage,
            emailSendPreviewTo: email,
            type: schedule,
            thresholdValueDollar,
            thresholdValuePercentage,
        });
    };
    const sendPreviewSlackAction = (channelId) => {
        sendPreviewSlack?.({
            emailMessage,
            slackChannelId: channelId,
            type: schedule,
            thresholdValueDollar,
            thresholdValuePercentage,
        });
    };
    const downloadFile = (fileBase64, filename = `${downloadFilenamePrefix}_${Date.now()}.csv`) => {
        if (!fileBase64) return;
        try {
            const byteCharacters = atob(fileBase64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'text/csv;charset=utf-8' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to download file', err);
        }
    };

    useEffect(() => {
        const currentDate = new Date();
        const nowTime = `${String(currentDate.getHours()).padStart(2, '0')}:${String(currentDate.getMinutes()).padStart(2, '0')}`;
        setEmailRecipients(arrayHasItems(defaultSettings?.emailRecipients) ? defaultSettings?.emailRecipients : []);
        setSchedule(defaultSettings?.scheduleType || "monthly");
        setSlackChannelId(defaultSettings?.slackChannelId || '');
        setDayOfMonth(defaultSettings?.dayOfMonth || 1);
        setDayOfWeek(defaultSettings?.dayOfWeek || 1);
        setTimeOfDay(defaultSettings?.timeOfDay || nowTime);
        setEmailMessage(defaultSettings?.emailMessage || '');
        setEmailSendPreviewTo(defaultSettings?.emailSendPreviewTo || '');
        setThresholdValueDollar(defaultSettings?.thresholdValueDollar || 0);    
        setThresholdValuePercentage(defaultSettings?.thresholdValuePercentage || 0);    
        setSlackSendPreviewTo('');
        setNotificationsEnabled(defaultSettings?.notificationsEnabled !== false);
    }, [isOpen, defaultSettings]);

    // Send History pagination (client-side only)
    const HISTORY_PAGE_SIZE = 5;
    const [historyPage, setHistoryPage] = useState(1);
    
    // Filter send history to exclude PREVIEW items when hidePreview is true
    const filteredHistory = useMemo(() => {
        if (!sendHistory || !arrayHasItems(sendHistory)) return [];
        if (hidePreview) {
            return sendHistory.filter(item => item.type !== 'PREVIEW' && item.type !== 'SLACK_PREVIEW');
        }
        return sendHistory;
    }, [sendHistory, hidePreview]);
    
    useEffect(() => {
        setHistoryPage(1);
    }, [sendHistory, hidePreview]);
    
    const totalHistoryPages = Math.max(1, Math.ceil((filteredHistory?.length || 0) / HISTORY_PAGE_SIZE));
    const paginatedHistory = useMemo(() => {
        const start = (historyPage - 1) * HISTORY_PAGE_SIZE;
        return (filteredHistory || []).slice(start, start + HISTORY_PAGE_SIZE);
    }, [filteredHistory, historyPage]);
    const fillerCount = Math.max(0, HISTORY_PAGE_SIZE - (paginatedHistory?.length || 0));
    const ROW_HEIGHT_CLASS = "h-12";

    return (
        <Drawer 
            size={"lg"} 
            isOpen={isOpen} 
            onClose={onClose} 
            title={'Report Settings'} 
            submitButtonText={'Save Changes'} 
            showActionButtons={true}
            width="700px"
            marginFromSidebar={marginFromSidebar}
            onSubmit={() => {
                onSuccessSubmit?.({
                    emailRecipients,
                    schedule,
                    slackChannelId,
                    dayOfMonth,
                    dayOfWeek,
                    timeOfDay,
                    emailMessage,
                    emailSendPreviewTo,
                    thresholdValueDollar,
                    thresholdValuePercentage,
                });
            }}
        >
            {isLoading && <OverlayGrayBackground />}
            <div className="p-6">
                <p className="text-sm text-gray-500">Configure email recipients, Google Sheets destinations and scheduling for this report</p>
                <div className="flex flex-col gap-4 mt-4">
                    {reportType && (
                        <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
                            <div className="flex flex-col">
                                <span className="text-base font-medium text-gray-900">Notifications</span>
                                <span className="text-xs text-gray-500">Turn scheduled email, Slack and Google Sheets runs for this report on or off</span>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={notificationsEnabled}
                                disabled={isSavingNotifications}
                                onClick={handleToggleNotifications}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-60 ${notificationsEnabled ? 'bg-black' : 'bg-gray-300'}`}
                            >
                                <span
                                    aria-hidden="true"
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${notificationsEnabled ? 'translate-x-5' : 'translate-x-0'}`}
                                />
                            </button>
                        </div>
                    )}
                    <div className="flex flex-col gap-2">
                        <label htmlFor="email" className="block text-base font-medium text-gray-900">Email Recipients</label>
                        <div className="flex items-center gap-2">
                            <input 
                                type="email" 
                                id="email" 
                                value={emailRecipientInput}
                                onChange={(e) => {
                                    setErrors((prev) => ({ ...prev, email: null })); 
                                    setEmailRecipientInput(e.target.value);
                                }}
                                placeholder="Enter email address"
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" 
                            />
                            <button 
                                className="transition-all duration-300 bg-primary cursor-pointer text-white px-4 py-2 rounded-md hover:bg-gray-500"
                                onClick={() => {
                                    const email = emailRecipientInput.trim().replace(/,$/, "");
                                    if (email && validateEmail(email) && !emailRecipients.includes(email)) {
                                        setEmailRecipients([...emailRecipients, email]);
                                        setEmailRecipientInput('');
                                    } else {
                                        setErrors((prev) => ({ ...prev, email: 'Invalid email address' }));
                                    }
                                }}
                            >
                                Add
                            </button>
                        </div>
                        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                        {emailRecipients.map((email, index) => (
                            <div key={index} className="bg-primary text-white px-2 py-1 rounded-md flex items-center">
                                {email}
                                <XCircleIcon className="h-4 w-4 ml-2 cursor-pointer" onClick={() => setEmailRecipients(emailRecipients.filter((_, i) => i !== index))} />
                            </div>
                        ))}
                        <p className="text-xs text-gray-500">Add multiple email addresses to receive this report</p>
                    </div>
                    <div className="flex flex-col gap-2">
                        <label htmlFor="slack-channel" className="block text-base font-medium text-gray-900">Slack Channel ID</label>
                        <input
                            type="text"
                            id="slack-channel"
                            value={slackChannelId}
                            onChange={(e) => setSlackChannelId(e.target.value)}
                            placeholder="Enter slack channel ID"
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                    </div>
                    {reportType && SCHEDULABLE_REPORTS[reportType] && (
                        <>
                            <div className="h-[1px] bg-gray-300 w-full" />
                            <ReportSheetSyncSettings reportType={reportType} handlerParams={sheetSyncHandlerParams} />
                            <div className="h-[1px] bg-gray-300 w-full" />
                        </>
                    )}
                    {reportType === SHIPPING_EACH_COST_CHANGE && (
                        <section className="flex flex-col gap-2">
                            <label htmlFor="slack-channel" className="block text-base font-medium text-gray-900">Thresholds</label>
                            <section className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                    <label htmlFor="threshold-type-dollar" className="w-[80px] block text-sm font-medium text-gray-900">$ Change ≥</label>
                                    <input
                                        type="number"
                                        value={thresholdValueDollar}
                                        onChange={(e) => {
                                            let value = e.target.value;
                                            if (!/^[0-9.]*$/.test(value)) return;
                                            if (value.startsWith(".")) return;
                                            if ((value.match(/\./g) || []).length > 1) return;
                                            if (/^0\d+/.test(value)) {
                                                value = value.replace(/^0+/, "");
                                            }
                                            setThresholdValueDollar(value);
                                        }}
                                        placeholder="Enter threshold value"
                                        className="w-36 rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <label htmlFor="threshold-type-percentage" className="w-[80px] block text-sm font-medium text-gray-900">% Change ≥</label>
                                    <input
                                        type="number"
                                        value={thresholdValuePercentage}
                                        onChange={(e) => {
                                            let value = e.target.value;
                                            if (!/^[0-9.]*$/.test(value)) return;
                                            if (value.startsWith(".")) return;
                                            if ((value.match(/\./g) || []).length > 1) return;
                                            if (/^0\d+/.test(value)) {
                                                value = value.replace(/^0+/, "");
                                            }
                                            setThresholdValuePercentage(value);
                                        }}
                                        placeholder="Enter threshold value"
                                        className="w-36 rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                    />
                                </div>
                            </section>
                        </section>
                    )}
                    <div className="flex flex-col gap-2">
                        <label htmlFor="schedule" className="block text-base font-medium text-gray-900">Send Schedule</label>
                        <div className="w-36 relative">
                            <SelectDropdown
                                options={[
                                    { label: "Monthly", value: "monthly" },
                                    { label: "Weekly", value: "weekly" },
                                    ...(dailyOptionEnabled ? [{ label: "Daily", value: "daily" }] : []),
                                ]}
                                value={schedule}
                                onChange={(value) => setSchedule(value)}
                                selectClassname="!w-36 !bg-white"
                            />
                        </div>
                    </div>
                    {schedule === "monthly" && (
                        <div className="flex flex-col gap-2">
                            <label htmlFor="schedule" className="block text-base font-medium text-gray-900">Day of Month</label>
                            <div className="w-36 relative">
                                <SelectDropdown
                                    options={daysOfMonth}
                                    value={dayOfMonth}
                                    onChange={(value) => setDayOfMonth(value)}
                                    selectClassname="!w-36 !bg-white"
                                />
                            </div>
                        </div>
                    )}
                    {schedule === "weekly" && (
                        <>
                            <div className="flex flex-col gap-2">
                                <label htmlFor="schedule" className="block text-base font-medium text-gray-900">Day of Week</label>
                                <div className="w-36 relative">
                                    <SelectDropdown
                                        options={daysOfWeek}
                                        value={dayOfWeek}
                                        onChange={(value) => setDayOfWeek(value)}
                                        selectClassname="!w-36 !bg-white"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    {(schedule === "monthly" || schedule === "weekly" || schedule === "daily") && (
                        <>
                            <div className="flex flex-col gap-2">
                                <label htmlFor="schedule" className="block text-base font-medium text-gray-900">Time of Day</label>
                                <div className="w-36 relative">
                                    <input
                                        type="time"
                                        value={timeOfDay}
                                        onChange={(e) => setTimeOfDay(e.target.value)}
                                        className="w-36 rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                    />
                                </div>
                            </div>
                            {schedule === "monthly" && <p className="text-xs text-gray-500">{`Email will be sent at ${timeOfDay} by timezone America/Denver on the day ${dayOfMonth}.`}</p>}
                            {schedule === "weekly" && <p className="text-xs text-gray-500">{`Email will be sent every week on ${daysOfWeek.find((day) => day.value === dayOfWeek)?.label} at ${timeOfDay} by timezone America/Denver.`}</p>}
                            {schedule === "daily" && <p className="text-xs text-gray-500">{`Email will be sent every day at ${timeOfDay} by timezone America/Denver.`}</p>}
                        </>
                    )}
                    <div className="flex flex-col gap-2">
                        <label htmlFor="schedule" className="block text-base font-medium text-gray-900">Email Message</label>
                        <textarea
                            id="email-message"
                            value={emailMessage}
                            onChange={(e) => setEmailMessage(e.target.value)}
                            placeholder="Enter email message"
                            className="w-full resize-none min-h-[130px] rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                        <p className="text-xs text-gray-500">A CSV of this report will be attached automatically.</p>
                    </div>
                    <div className="h-[1px] bg-gray-300 w-full" />
                    <div className="flex flex-col gap-2">
                        <label htmlFor="email-subject" className="block text-base font-medium text-gray-900">Preview</label>
                        <span className="flex items-center gap-2 font-medium select-none text-gray-500 text-sm">Send Preview To</span>
                        <input
                            type="email"
                            id="email-subject"
                            value={emailSendPreviewTo}
                            onChange={(e) => {
                                setErrors((prev) => ({ ...prev, previewEmail: null }));
                                setEmailSendPreviewTo(e.target.value.replace(/['"]/g, ""));
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "'" || e.key === '"') {
                                    e.preventDefault();
                                }
                            }}
                            placeholder="Enter email address"
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                        {errors.previewEmail && <p className="text-red-500 text-sm mt-1">{errors.previewEmail}</p>}
                        <button
                            onClick={() => {
                                const email = emailSendPreviewTo.trim().replace(/,$/, "");
                                if (email && validateEmail(email)) {
                                    sendPreviewEmailAction(email);
                                } else {
                                    setErrors((prev) => ({ ...prev, previewEmail: 'Invalid email address' }));
                                }
                            }}
                            className="transition-all duration-300 bg-primary cursor-pointer text-white px-4 py-2 rounded-md hover:bg-gray-500"
                        >
                            Send Preview
                        </button>
                        <div className="h-[1px] bg-gray-200 w-full my-2" />
                        <span className="flex items-center gap-2 font-medium select-none text-gray-500 text-sm">Send Slack Preview To</span>
                        <input
                            type="text"
                            value={slackSendPreviewTo}
                            onChange={(e) => {
                                setErrors((prev) => ({ ...prev, previewSlack: null }));
                                setSlackSendPreviewTo(e.target.value.replace(/['"]/g, ""));
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "'" || e.key === '"') {
                                    e.preventDefault();
                                }
                            }}
                            placeholder="Enter slack channel ID"
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            disabled={!sendPreviewSlack}
                        />
                        {!sendPreviewSlack && (
                            <p className="text-xs text-gray-400">Slack preview is not available for this report yet.</p>
                        )}
                        {errors.previewSlack && <p className="text-red-500 text-sm mt-1">{errors.previewSlack}</p>}
                        <button
                            onClick={() => {
                                const channelId = String(slackSendPreviewTo || '').trim().replace(/,$/, "");
                                if (!sendPreviewSlack) return;
                                if (channelId) {
                                    sendPreviewSlackAction(channelId);
                                } else {
                                    setErrors((prev) => ({ ...prev, previewSlack: 'Slack channel ID is required' }));
                                }
                            }}
                            className={`transition-all duration-300 px-4 py-2 rounded-md ${
                                sendPreviewSlack ? 'bg-primary cursor-pointer text-white hover:bg-gray-500' : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            }`}
                            disabled={!sendPreviewSlack}
                        >
                            Send Slack Preview
                        </button>
                    </div>
                    <div className="h-[1px] bg-gray-300 w-full" />
                    {isLoadingSendHistory && <LoaderSpinner width="40" height="40" border="3" colorSpinner="black" />}
                    {!isLoadingSendHistory && arrayHasItems(sendHistory) && (
                        <>
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                    <label htmlFor="email-subject" className="block text-base font-medium text-gray-900">Send History</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="hide-preview"
                                            checked={hidePreview}
                                            onChange={(e) => setHidePreview(e.target.checked)}
                                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                        />
                                        <label htmlFor="hide-preview" className="text-sm text-gray-700 cursor-pointer select-none">
                                            Hide Preview
                                        </label>
                                    </div>
                                </div>
                                {!arrayHasItems(filteredHistory) ? (
                                    <div className="text-sm text-gray-500 text-center py-4">
                                        No history items to display{hidePreview ? ' (all preview items are hidden)' : ''}.
                                    </div>
                                ) : (
                                    <div className="rounded-lg border border-border overflow-hidden">
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm sidebar-table">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px]">Send At</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px]" style={{ flex: '0 0 100px' }}>Type</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[220px]">Sent To</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px]">Status</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[80px]">file</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {paginatedHistory.map((item) => (
                                                    <tr key={item.id} className="border-b border-border last:border-0">
                                                        <td className={`px-3 py-3 text-xs whitespace-nowrap align-middle w-[150px] ${ROW_HEIGHT_CLASS}`}>
                                                            <div>{item.sentDate}</div>
                                                            <div className="text-xs text-gray-500">{item.sentTime}</div>
                                                        </td>
                                                        <td className={`px-3 py-3 text-xs whitespace-nowrap align-middle w-[100px] ${ROW_HEIGHT_CLASS}`}>
                                                            <div className={`text-xs ${getTypeColor(item.type)} px-2 py-1 rounded-lg text-center`}>
                                                                {getTypeLabel(item.type)}
                                                            </div>
                                                        </td>
                                                        <td className={`px-3 py-3 text-xs whitespace-nowrap align-middle w-[220px] ${ROW_HEIGHT_CLASS}`}>
                                                            <span className="flex items-center gap-1 w-[220px]">
                                                                <span className="block truncate">
                                                                    {arrayHasItems(item.sentTo)
                                                                        ? item.sentTo.length === 1
                                                                            ? item.sentTo[0]
                                                                            : `${item.sentTo.length} recipients`
                                                                        : ""}
                                                                </span>
                                                                {arrayHasItems(item.sentTo) && item.sentTo.length > 1 && (
                                                                    <span className="flex items-center">
                                                                        <InformationCircleIcon
                                                                            className="h-5 w-5 text-primary cursor-pointer"
                                                                            data-tip
                                                                            data-for={`report-sentto-${item.id}`}
                                                                        />
                                                                        <ReactTooltip id={`report-sentto-${item.id}`} effect="solid" place="top">
                                                                            <div className="max-w-xs whitespace-normal break-words">
                                                                                {item.sentTo.map((email, idx) => (
                                                                                    <div key={`${item.id}-sentto-${idx}`}>{email}</div>
                                                                                ))}
                                                                            </div>
                                                                        </ReactTooltip>
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </td>
                                                        <td className={`px-3 py-3 text-xs whitespace-nowrap align-middle w-[100px] ${ROW_HEIGHT_CLASS}`}>
                                                            <div className="flex items-center gap-2">
                                                                <div className={`text-white w-12 text-center text-xs px-2 py-1 rounded-md ${item.status === "SENT" ? 'bg-primary' : 'bg-red-500'}`}>
                                                                    {SendHistoryStatus[item.status] || ""}
                                                                </div>
                                                                {item.error && (
                                                                     <div className="flex items-center">
                                                                         <InformationCircleIcon
                                                                             className="h-4 w-4 text-red-500"
                                                                             data-tip
                                                                             data-for={`report-err-${item.id}`}
                                                                         />
                                                                         <ReactTooltip
                                                                             id={`report-err-${item.id}`}
                                                                             effect="solid"
                                                                             place="top"
                                                                         >
                                                                             <div className="w-48 whitespace-normal break-words">
                                                                                 {item.error}
                                                                             </div>
                                                                         </ReactTooltip>
                                                                     </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className={`px-3 py-3 text-xs whitespace-nowrap align-middle w-[80px] ${ROW_HEIGHT_CLASS}`}>
                                                            {!item.error && item.file && (
                                                                <button
                                                                    onClick={() => {
                                                                        downloadFile(item.file, `${downloadFilenamePrefix}_${item.id}.csv`);
                                                                    }}
                                                                    className="transition-all duration-300 bg-transparent cursor-pointer text-gray-500 px-2 py-1 rounded-md hover:bg-gray-100"
                                                                >
                                                                    <ArrowDownTrayIcon className="h-4 w-4" />
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {Array.from({ length: fillerCount }).map((_, i) => (
                                                    <tr key={`filler-${i}`} className="border-b border-border last:border-0 bg-gray-50 h-[56px]">
                                                        <td className={`px-3 py-3 text-xs whitespace-nowrap align-middle ${ROW_HEIGHT_CLASS}`}></td>
                                                        <td className={`px-3 py-3 text-xs whitespace-nowrap align-middle ${ROW_HEIGHT_CLASS}`}></td>
                                                        <td className={`px-3 py-3 text-xs whitespace-nowrap align-middle ${ROW_HEIGHT_CLASS}`}></td>
                                                        <td className={`px-3 py-3 text-xs whitespace-nowrap align-middle ${ROW_HEIGHT_CLASS}`}></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-white">
                                        <span className="text-xs text-gray-500">Page {historyPage} of {totalHistoryPages}</span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-50"
                                                disabled={historyPage === 1}
                                                onClick={() => setHistoryPage(1)}
                                            >
                                                First
                                            </button>
                                            <button
                                                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-50"
                                                disabled={historyPage === 1}
                                                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                                            >
                                                Prev
                                            </button>
                                            <button
                                                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-50"
                                                disabled={historyPage === totalHistoryPages}
                                                onClick={() => setHistoryPage((p) => Math.min(totalHistoryPages, p + 1))}
                                            >
                                                Next
                                            </button>
                                            <button
                                                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-50"
                                                disabled={historyPage === totalHistoryPages}
                                                onClick={() => setHistoryPage(totalHistoryPages)}
                                            >
                                                Last
                                            </button>
                                        </div>
                                    </div>
                                    </div>
                                )}
                            </div>
                        </> 
                    )}
                </div>
            </div>
        </Drawer>
    );
};